/**
 * Tasks Page
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchTasks,
  updateTask,
  fetchTask,
  createSubtask,
  updateSubtask,
  fetchAllSubtasks
} from '@/store/taskSlice';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import type { Subtask } from '@/types';
import { fetchProjects } from '@/store/projectSlice';
import { TaskCard } from '@/components/TaskCard';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import { CreateTaskModal } from '@/components/CreateTaskModal';
import { Plus, Filter } from 'lucide-react';
import { Task, Project } from '@/types';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { commentAPI } from '@/api/commentAPI';
import { mastersAPI, TaskType, Priority, WorkflowStatus} from '@/api/mastersApi';
import { CommentCreateData, Comment } from '@/types/comments';
import { deleteComment, formatCommentsForActivity, updateComment } from '@/utils/commentUtils';
import { ensureHtml, toPlainText } from '@/utils/richText';
import { Loader } from '@/components/Loader';
import toast from 'react-hot-toast';
import { userAPI } from '@/api/userAPI';
import { shareAPI } from '@/api/shareAPI';
import Share from '@/components/Share';
import { subtaskAPI } from '@/api/subtaskAPI';
import { useProjectPermissions } from '@/hooks/useProjectPermissions';

export const Tasks = () => {
  const dispatch = useAppDispatch();
  const {
    tasks,
    loading,
    currentTask,
    subtasks
  } = useAppSelector((state) => state.tasks);
  const { projects } = useAppSelector((state) => state.projects);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);

  // Subtask Modal States
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<Task | null>(null);

  // Master data states
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);

  // Dynamic data states
  const [_, setRelatedWork] = useState<any>({ blocks: [], relatesTo: [] });
  const [activity, setActivity] = useState<any[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [filters, setFilters] = useState({
    project_id: '',
    workflow_status_id: '',
    priority_id: '',
  });

  // Project-level permissions: viewer should be read-only within that project
  const { isViewerOnly } = useProjectPermissions(filters.project_id || null);

  // (projectViewerStatus is now a useMemo derived from project.my_role —
  // no separate state or per-project API calls needed.)
  const { user: currentUser } = useAppSelector((state) => state.auth);
  // Share popover state (same as ProjectHeader)
  const [isShareOpen, setIsShareOpen] = useState(false);
  const shareAnchorRef = useRef<HTMLButtonElement | null>(null);
  const shareRef = useRef<HTMLDivElement | null>(null);
  const [_1, setShareLeft] = useState<number>(0);
  const [_2, setShareTop] = useState<number>(0);
  const [_3, setShareWidth] = useState<number>(420);
  const [shareTo, setShareTo] = useState('');
  const [shareMsg, setShareMsg] = useState('');
  const [_4, setDropdownOpen] = useState(false);
  const [_6, setResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [_5, setResultsLoading] = useState(false);
  const [recipients, setRecipients] = useState<Array<{ id?: string; email: string; name?: string }>>([]);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const { user } = useAppSelector((state) => state.auth);

  const projectMap = useMemo(() => {
    return projects.reduce((acc, project) => {
      if (project?.id) {
        acc[project.id] = project as Project;
      }
      return acc;
    }, {} as Record<string, Project>);
  }, [projects]);

  // Stable derived values — memoised so effects only re-run when the actual
  // set of IDs changes, not on every Redux array-reference change.
  const projectIds = useMemo(() =>
    projects
      .filter((p: any) => p.is_active !== false)
      .map((p: any) => p.id)
      .filter(Boolean),
    [projects]
  );
  const projectIdsKey = projectIds.join(',');

  // Memoised so expensive spread+sort doesn't repeat on every render
  const subtasksAsTasks = useMemo(() => subtasks.map((subtask: any) => {
    const parentTask = tasks.find(t => t.id === subtask.task_id);
    return {
      ...subtask,
      project_id: parentTask?.project_id || '',
      workflow_status_id: subtask.workflow_status_id,
      priority_id: subtask.priority_id,
      parent_task_id: subtask.task_id,
      reporter_id: '',
      display_number: parentTask?.display_number ?? subtask.id?.substring(0, 6),
      status: (subtask.status as any) || 'todo',
      priority: (subtask.priority as any) || 'medium',
      isSubtask: true,
    };
  }), [subtasks, tasks]);

  // Combine tasks and subtasks, apply filters, then sort by created_at (newest first)
  const filteredTasks = useMemo(() => [...tasks, ...subtasksAsTasks]
    .map(item => {
      const project = projectMap[(item as any).project_id];
      return { ...item, project, projectKey: project?.key || '' };
    })
    .filter(item => {
      const projectId = (item as any).project_id;
      if (filters.project_id && projectId !== filters.project_id) return false;
      if (filters.workflow_status_id && (item as any).workflow_status_id !== filters.workflow_status_id) return false;
      if (filters.priority_id && (item as any).priority_id !== filters.priority_id) return false;
      return true;
    })
    .sort((a, b) => {
      const aCreated = new Date((a as any).created_at || 0).getTime();
      const bCreated = new Date((b as any).created_at || 0).getTime();
      return bCreated - aCreated;
    }),
    [tasks, subtasksAsTasks, projectMap, filters.project_id, filters.workflow_status_id, filters.priority_id]
  );

  // Helper to convert empty string filters to undefined for server params
  const buildServerFilters = (f: { project_id: string; workflow_status_id: string; priority_id: string }) => {
    const out: any = {};
    if (f.project_id) out.project_id = f.project_id;
    if (f.workflow_status_id) out.workflow_status_id = f.workflow_status_id;
    if (f.priority_id) out.priority_id = f.priority_id;
    return out;
  };

  useEffect(() => {
    dispatch(fetchProjects({ is_active: true, organization_id: user?.organization_id }));
  }, [dispatch]);

  // useEffect(() => {
  //   if (!projects.length) return;  // wait until projects are fetched
  // const projectIds = projects.map(p => p.id).filter(Boolean) as string[];
  // dispatch(fetchTasks({ project_ids: projectIds }));
  //   dispatch(fetchAllSubtasks({}));

  //   // Fetch master data
  //   (async () => {
  //     try {
  //       const types = await mastersAPI.getTaskTypes();
  //       setTaskTypes(types.filter(t => t.is_active !== false));
  //     } catch { }
  //     try {
  //       const prios = await mastersAPI.getPriorities();
  //       setPriorities(prios.filter(p => p.is_active !== false).sort((a, b) => a.order - b.order));
  //     } catch { }
  //     try {
  //       const statuses = await mastersAPI.getWorkflowStatuses();
  //       setWorkflowStatuses(statuses.sort((a, b) => a.order - b.order));
  //     } catch { }
  //   })();
  // }, [dispatch]);


  useEffect(() => {
    if (!projectIdsKey) return;
    (async () => {
      try {
        const types = await mastersAPI.getTaskTypes();
        setTaskTypes(types.filter(t => t.is_active !== false));
      } catch { }
      try {
        const prios = await mastersAPI.getPriorities();
        setPriorities(prios.filter(p => p.is_active !== false).sort((a, b) => a.order - b.order));
      } catch { }
      try {
        const statuses = await mastersAPI.getWorkflowStatuses();
        setWorkflowStatuses(statuses.sort((a, b) => a.order - b.order));
      } catch { }
    })();
  }, [projectIdsKey]);

  useEffect(() => {
    if (!projectIdsKey) return;
    if (!filters.project_id) {
      dispatch(fetchTasks({ project_ids: projectIds }));
      dispatch(fetchAllSubtasks({ project_ids: projectIds }));
    } else {
      dispatch(fetchTasks(buildServerFilters(filters)));
      dispatch(fetchAllSubtasks(buildServerFilters(filters)));
    }
  }, [dispatch, projectIdsKey, filters.project_id, filters.workflow_status_id, filters.priority_id]);
  const openShare = () => {
    try {
      const rect = shareAnchorRef.current?.getBoundingClientRect();
      if (rect) {
        const desired = Math.min(440, Math.max(360, window.innerWidth - 24));
        setShareWidth(desired);
        let left = Math.max(12, Math.min(rect.left, window.innerWidth - desired - 12));
        setShareLeft(Math.round(left));
        setShareTop(Math.round(rect.bottom + 8));
      }
    } catch { }
    setIsShareOpen(true);
  };

  // Position/close events for share
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!isShareOpen) return;
      if (
        shareRef.current && !shareRef.current.contains(e.target as Node) &&
        shareAnchorRef.current && !shareAnchorRef.current.contains(e.target as Node)
      ) {
        setIsShareOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsShareOpen(false); };
    const onPos = () => {
      if (!isShareOpen) return;
      const rect = shareAnchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const desired = Math.min(440, Math.max(360, window.innerWidth - 24));
      setShareWidth(desired);
      let left = Math.max(12, Math.min(rect.left, window.innerWidth - desired - 12));
      setShareLeft(Math.round(left));
      setShareTop(Math.round(rect.bottom + 8));
    };
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onPos);
    window.addEventListener('scroll', onPos, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onPos);
      window.removeEventListener('scroll', onPos, true);
    };
  }, [isShareOpen]);

  const isValidEmail = (v: string) => /.+@.+\..+/.test(v.trim());

  // Load default users and search
  useEffect(() => {
    if (!isShareOpen) return;
    const q = shareTo.trim();
    const fetchDefault = async () => {
      try {
        setResultsLoading(true);
        const users = await userAPI.getUsers({ is_active: true, limit: 20 });
        const mapped = (users || []).map((u: any) => ({
          id: String(u.id),
          name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email,
          email: u.email,
        }));
        setResults(mapped);
        setDropdownOpen(true);
      } catch {
        setResults([]);
      } finally {
        setResultsLoading(false);
      }
    };

    const search = () => {
      const t = setTimeout(async () => {
        try {
          setResultsLoading(true);
          const res = await userAPI.searchUsers(q);
          const mapped = (res || []).map((u: any) => ({ id: String(u.id), name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email, email: u.email }));
          setResults(mapped);
          setDropdownOpen(true);
        } catch {
          setResults([]);
        } finally {
          setResultsLoading(false);
        }
      }, 250);
      return () => clearTimeout(t);
    };

    if (q.length < 2) { fetchDefault(); return; }
    const cleanup = search();
    return cleanup;
  }, [shareTo, isShareOpen]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) setDropdownOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const addRecipient = (rec: { id?: string; email: string; name?: string }) => {
    if (recipients.find(r => r.email.toLowerCase() === rec.email.toLowerCase())) return;
    setRecipients(prev => [...prev, rec]);
    setShareTo('');
    setDropdownOpen(false);
  };

  const removeRecipient = (email: string) => setRecipients(prev => prev.filter(r => r.email.toLowerCase() !== email.toLowerCase()));

  const submitShare = () => {
    const pending = shareTo.trim();
    let to = recipients.map(r => r.email);
    if (isValidEmail(pending)) to = [...to, pending];
    if (to.length === 0) { toast.error('Please add at least one recipient'); return; }
    const subject = 'Shared view: Tasks';
    const link = window.location.href;
    (async () => {
      try {
        await shareAPI.send({ recipients: to, subject, message: shareMsg, link });
        toast.success('Share email sent');
        setIsShareOpen(false); setRecipients([]); setShareTo(''); setShareMsg('');
      } catch { toast.error('Failed to send share email'); }
    })();
  };

  // Refetch tasks from DB whenever filters change
  // useEffect(() => {
  //   dispatch(fetchTasks(buildServerFilters(filters)));
  //   dispatch(fetchAllSubtasks(buildServerFilters(filters)));
  // }, [dispatch, filters.project_id, filters.workflow_status_id, filters.priority_id]);

  // Load project members when a project is selected in filters
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!filters.project_id) {
        // If no project is selected, clear project members or set to empty
        if (mounted) setProjectMembers([]);
        return;
      }

      try {
        const data = await projectMemberAPI.getProjectMembers(filters.project_id);
        if (mounted) setProjectMembers(data);
      } catch (error) {
        console.error('Failed to fetch project members:', error);
        if (mounted) setProjectMembers([]);
      }
    })();
    return () => { mounted = false; };
  }, [filters.project_id]);

  // Viewer-only status per project, derived directly from project.my_role
  // returned by the projects API — no per-project API calls.
  const projectViewerStatus = useMemo(() => {
    const isAdminLike = currentUser?.role?.toLowerCase() === 'admin' || currentUser?.role?.toLowerCase() === 'super_admin';
    const map: Record<string, boolean> = {};
    if (!currentUser?.id) return map;
    projects.forEach((p: any) => {
      if (!p?.id) return;
      const role = (p?.my_role || '').toLowerCase();
      map[p.id] = !isAdminLike && role === 'viewer';
    });
    return map;
  }, [projects, currentUser?.id, currentUser?.role]);

  // Helper function to refresh project members
  const refreshProjectMembers = async (projectId?: string) => {
    const targetProjectId = projectId || filters.project_id;
    if (!targetProjectId) {
      setProjectMembers([]);
      return;
    }
    try {
      const data = await projectMemberAPI.getProjectMembers(targetProjectId);
      setProjectMembers(data);
    } catch (error) {
      console.error('Failed to fetch project members:', error);
      setProjectMembers([]);
    }
  };


  // Handle task card click to open task detail modal
  const handleTaskClick = async (task: Task) => {
    setSelectedTask(task);
    
    // Always refresh project members BEFORE opening modal to ensure sidebar is functional
    // Use task.project_id if available, otherwise fall back to filters.project_id
    const projectId = task.project_id || filters.project_id;
    if (projectId) {
      await refreshProjectMembers(projectId);
    } else if (task.project_id) {
      // If task has project_id but filters don't, still load members for that project
      await refreshProjectMembers(task.project_id);
    }
    
    // Open modal after project members are loaded
    setIsTaskModalOpen(true);
    
    // Fetch dynamic data for the task using Redux
    try {
      await Promise.all([
        dispatch(fetchTask(task.id)),
        loadComments(task.id)
        // dispatch(fetchSubtasks(task.id))
      ]);

      // Fetch related work and activity (you'll need to implement these in your API)
      const [relatedWorkData, activityData] = await Promise.all([
        fetchRelatedWork(task.id),
        fetchTaskActivity(task.id)
      ]);

      setRelatedWork(relatedWorkData);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
    }
  };

  // Sync selectedTask with updated task from Redux state after status update
  // Only sync if the currentTask matches the selectedTask (same task)
  useEffect(() => {
    if (selectedTask && currentTask && currentTask.id === selectedTask.id) {
      setSelectedTask(currentTask);
    }
  }, [currentTask, selectedTask?.id]);

  // Handle task modal close
  const handleTaskModalClose = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
    setRelatedWork({ blocks: [], relatesTo: [] });
    setActivity([]);
    setComments([]);

  };

  const loadComments = async (taskId: string) => {
    try {
      const commentsData = await commentAPI.getComments(taskId);
      setComments(commentsData);

      // Use the utility function to format comments
      const activityData = formatCommentsForActivity(commentsData);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
      setActivity([]);
    }
  };

  const openSubtaskModal = (subtask: Subtask, parent: Task) => {
    setParentTaskForSubtask(parent);
    setIsTaskModalOpen(false);
    setActiveSubtask(subtask);
    setIsSubtaskModalOpen(true);
  };

  const closeSubtaskModal = () => {
    setIsSubtaskModalOpen(false);
    setActiveSubtask(null);
    setParentTaskForSubtask(null);
  };
  // API handlers using Redux
  const handleStatusUpdate = async (taskId: string, newStatus: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: newStatus || undefined }
      })).unwrap();

      // Refresh tasks list
      dispatch(fetchTasks(buildServerFilters(filters)));
      dispatch(fetchAllSubtasks(buildServerFilters(filters)));

      // Update selectedTask/currentTask to reflect the status change
      if (selectedTask?.id === taskId || currentTask?.id === taskId) {
        dispatch(fetchTask(taskId));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const addComment = async (taskId: string, html: string, parentId?: string, plainText?: string) => {
    const contentHtml = html;
    const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
    const commentData: CommentCreateData = {
      content: contentText,
      content_html: contentHtml,
      content_text: contentText,
      task_id: taskId,
      parent_comment_id: parentId
    };
    return await commentAPI.createComment(commentData);
  };

  const handleAddComment = async (taskId: string, html: string, parentId?: string, plainText?: string) => {
    try {
      const newComment = await addComment(taskId, html, parentId, plainText);

      // Reload comments to refresh the list
      await loadComments(taskId);

      return newComment;
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  const handleAddSubtask = async (taskId: string, title: string) => {
    try {
      await dispatch(createSubtask({
        taskId,
        data: { title }
      })).unwrap();

      // Subtasks are automatically updated in Redux state
    } catch (error) {
      console.error('Failed to add subtask:', error);
    }
  };

  const handleUpdateSubtask = async (_taskId: string, subtaskId: string, updates: any) => {
    try {
      await dispatch(updateSubtask({
        subtaskId,
        data: updates
      })).unwrap();

      // Subtasks are automatically updated in Redux state
    } catch (error) {
      console.error('Failed to update subtask:', error);
    }
  };

  // Add these functions to your parent component
  const handleUpdateDescription = async (taskId: string, description: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { description }
      })).unwrap();

      // Refresh the current task
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update description:', error);
      throw error;
    }
  };

  const handleUpdateAssignee = async (taskId: string, assigneeId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { assigned_to: assigneeId === null ? null : assigneeId }
      })).unwrap();

      // Refresh tasks list and current task
      dispatch(fetchTasks(buildServerFilters(filters)));
      dispatch(fetchAllSubtasks(buildServerFilters(filters)));
      dispatch(fetchTask(taskId));
      
      // Refresh project members after assignee update to get latest list
      if (selectedTask?.project_id || currentTask?.project_id) {
        await refreshProjectMembers(selectedTask?.project_id || currentTask?.project_id);
      }
    } catch (error) {
      console.error('Failed to update assignee:', error);
      throw error;
    }
  };

  const handleUpdateReporter = async (taskId: string, reporterId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { reporter_id: reporterId === null ? null : reporterId }
      })).unwrap();

      // Refresh tasks list and current task
      dispatch(fetchTasks(buildServerFilters(filters)));
      dispatch(fetchAllSubtasks(buildServerFilters(filters)));
      dispatch(fetchTask(taskId));
      
      // Refresh project members after reporter update to get latest list
      if (selectedTask?.project_id || currentTask?.project_id) {
        await refreshProjectMembers(selectedTask?.project_id || currentTask?.project_id);
      }
    } catch (error) {
      console.error('Failed to update reporter:', error);
      throw error;
    }
  };

  // Prepare task details for the sidebar
  const getTaskDetails = () => {
    const task = selectedTask || currentTask;
    if (!task) return {};
    
    const rawLabels: string | string[] | null | undefined = (task as any)?.labels;
    
    return {
      assignee: task.assigned_to || 'Unassigned',
      labels: Array.isArray(rawLabels)
        ? rawLabels
        : typeof rawLabels === "string"
          ? rawLabels.split(",").map((label: string) => label.trim())
          : [],
      dueDate: task.due_date || 'Not set',
      parent: (task as any).parent_id || 'None',
      team: 'Development Team', // You might want to add this to your Task type
      testTeam: 'QA Team', // You might want to add this to your Task type
      sme: ['Janarthanan', 'karthick Balu', 'Rekha Kotteeswaran'], // This should come from your API
      sprint: task.sprint_id || 'Not set',
      originalEstimate: (task as any).estimate || 'Not set',
      timeRemaining: 'Not set', // You might want to calculate this
      development: 'In Progress', // This could be derived from status
      reporter: 'Janarthanan', // This should come from your API
      organization: 'Health Individual' // This should come from your API
    };
  };


  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
  };
  const handleDeleteComment = async (commentId: string) => {
    await deleteComment(commentId);
    // Reload activity if a task is currently open
    if (selectedTask?.id) {
      await loadComments(selectedTask.id);

      const activityData = await fetchTaskActivity(selectedTask.id);
      setActivity(activityData);
    }
  };

  const handleUpdateComment = async (commentId: string, html: string, plainText?: string) => {
    try {
      await updateComment(commentId, html, plainText);
      if (selectedTask?.id) {
        await loadComments(selectedTask.id);

        const activityData = await fetchTaskActivity(selectedTask.id);
        setActivity(activityData);
      }
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  // Helper function to fetch task activity (used in multiple handlers)
  const fetchTaskActivity = async (taskId: string) => {
    try {
      const comments = await commentAPI.getComments(taskId);
      return formatCommentsForActivity(comments);
    } catch (error) {
      console.error('Failed to fetch task activity:', error);
      return [];
    }
  };

const handleUpdateTitle = async (id: string, newTitle: string) => {
  const taskToUpdate = selectedTask || currentTask;
  if (!taskToUpdate) return;
  
  try {
    const taskId = id || taskToUpdate.id;
    await dispatch(updateTask({
      id: taskId,
      data: { title: newTitle }
    })).unwrap();
    // Refresh the current task
    dispatch(fetchTask(taskId));
    // Update selectedTask if it matches
    if (selectedTask && selectedTask.id === taskId) {
      dispatch(fetchTask(taskId));
    }
  } catch (error) {
    console.error('Failed to update title:', error);
    toast.error('Failed to update title');
    throw error;
  }
};

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tasks</h1>
          <p className="text-gray-600 mt-1">Manage all your tasks</p>
        </div>
        <div className="flex items-center gap-2 relative">
          <Share label="Share" className="btn btn-secondary flex items-center gap-2" subject="Shared view: Tasks" />
          {/* Show "New Task" button when "All Projects" is selected, or when a specific project is selected and user is not viewer-only */}
          {(!filters.project_id || !isViewerOnly) && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Task
            </button>
          )}
        </div>
      </div>
      {/* Shared Share component handles popover */}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={filters.project_id}
            onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
            className="input"
          >
            <option value="">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
{/* 
          <select
            value={filters.workflow_status_id}
            onChange={(e) => setFilters({ ...filters, workflow_status_id: e.target.value })}
            className="input"
          >
            <option value="">All Status</option>
            {workflowStatuses.map((status) => (
              <option key={status.id} value={status.id}>{status.name}</option>
            ))}
          </select> */}

          <select
            value={filters.priority_id}
            onChange={(e) => setFilters({ ...filters, priority_id: e.target.value })}
            className="input"
          >
            <option value="">All Priority</option>
            {priorities.map((priority) => (
              <option key={priority.id} value={priority.id}>{priority.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tasks Grid */}
      {loading ? (
        <Loader variant="overlay" size="lg" text={'Loading Tasks...'} />
      ) : filteredTasks.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          No tasks found.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTasks.map((item) => {
            const isSubtask = (item as any).isSubtask;
            const task = item as Task;
            const taskProjectId = (task as any).project_id;
            
            // Determine if user is viewer-only for this specific task's project
            // When "All Projects" is selected, check per-task project viewer status
            // When a specific project is selected, use the global isViewerOnly
            const taskIsViewerOnly = filters.project_id 
              ? isViewerOnly 
              : (taskProjectId ? (projectViewerStatus[taskProjectId] ?? false) : false);
            
            return (
              <TaskCard
                key={item.id}
                task={task}
                priorities={priorities}
                project={
                  (item as any).project ||
                  ((task as any).project_id ? projectMap[(task as any).project_id as string] : undefined)
                }
                onClick={() => {
                  if (isSubtask) {
                    // Open subtask modal
                    const subtask = subtasks.find(s => s.id === item.id);
                    // Get parent task ID from the subtask's task_id field (which refers to parent)
                    const parentTaskId = subtask?.task_id || (item as any).task_id || (item as any).parent_task_id;
                    const parentTask = tasks.find(t => t.id === parentTaskId);
                    if (subtask && parentTask) {
                      openSubtaskModal(subtask, parentTask);
                    }
                  } else {
                    // Open task modal
                    handleTaskClick(task);
                  }
                }}
                onDelete={() => {
                  dispatch(fetchTasks(buildServerFilters(filters)));
                  dispatch(fetchAllSubtasks(buildServerFilters(filters)));
                }}
                taskTypes={taskTypes}
                projectMembers={projectMembers}
                isViewerOnly={taskIsViewerOnly}
              />
            );
          })}
        </div>
      )}

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          // Refresh tasks with current filters after task creation
          dispatch(fetchTasks(buildServerFilters(filters)));
        }}
      />

      {/* Task Detail Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={handleTaskModalClose}
        task={selectedTask || currentTask}
        onStatusUpdate={handleStatusUpdate}
        onAddComment={handleAddComment}
        onUpdateDescription={handleUpdateDescription}
        onUpdateAssignee={handleUpdateAssignee}
        onUpdateReporter={handleUpdateReporter}
        activity={activity}
        taskDetails={getTaskDetails()}
        projectMembers={projectMembers} // Now properly defined
        comments={comments}
        onDeleteComment={handleDeleteComment}
        onUpdateComment={handleUpdateComment}
        onUpdateTaskTitle={handleUpdateTitle}
        workflowStatuses={workflowStatuses}
        onOpenSubtask={openSubtaskModal}
        priorities={priorities}
        projectKey={
          (() => {
            const task = selectedTask || currentTask;
            if (!task?.project_id) return undefined;
            const project = projectMap[task.project_id];
            return project?.key;
          })()
        }
        project={
          (() => {
            const task = currentTask || selectedTask;
            if (!task?.project_id) return undefined;
            return projectMap[task.project_id];
          })()
        }
        // Viewer can only see, not mutate
        // When "All Projects" is selected, check per-task project viewer status
        // When a specific project is selected, use the global isViewerOnly
        isViewerOnly={
          (() => {
            const task = selectedTask || currentTask;
            const taskProjectId = task?.project_id;
            if (!taskProjectId) return false;
            return filters.project_id 
              ? isViewerOnly 
              : (projectViewerStatus[taskProjectId] ?? false);
          })()
        }
      />

      {/* Subtask Modal */}
      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={closeSubtaskModal}
          parentKey={
            (() => {
              const task = parentTaskForSubtask || currentTask || selectedTask;
              if (!task?.project_id) return undefined;
              const project = projectMap[task.project_id];
              return `${project?.key}-${(parentTaskForSubtask as any)?.display_number ?? (currentTask as any)?.display_number ?? (selectedTask as any)?.display_number ?? ''}`;
            })()
          }
          parentTitle={(parentTaskForSubtask || currentTask || selectedTask)?.title}
          parentTask={(parentTaskForSubtask || currentTask || selectedTask) as any}
          onOpenParent={(_parent) => {
            closeSubtaskModal();
            if (_parent) {
              handleTaskClick(_parent);
            }
          }}
          projectMembers={projectMembers}
          activity={activity}
          projectKey={
            (() => {
              const task = parentTaskForSubtask || currentTask || selectedTask;
              if (!task?.project_id) return undefined;
              const project = projectMap[task.project_id];
              return project?.key;
            })()
          }
          project={
            (() => {
              const task = parentTaskForSubtask || selectedTask || currentTask;
              if (!task?.project_id) return undefined;
              return projectMap[task.project_id];
            })()
          }
          onAddComment={handleAddComment}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
          onSave={async (updates) => {
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates })).unwrap();
            // Refresh subtask data
            try {
              const fresh = await subtaskAPI.get(activeSubtask.id);
              setActiveSubtask(fresh);
            } catch (error) {
              console.error('Failed to refresh subtask:', error);
            }
          }}
          onDelete={async () => {
            setIsSubtaskModalOpen(false);
          }}
          // Viewer can only see, not mutate
          // When "All Projects" is selected, check per-task project viewer status
          // When a specific project is selected, use the global isViewerOnly
          isViewerOnly={
            (() => {
              const task = parentTaskForSubtask || currentTask || selectedTask;
              const taskProjectId = task?.project_id;
              if (!taskProjectId) return false;
              return filters.project_id 
                ? isViewerOnly 
                : (projectViewerStatus[taskProjectId] ?? false);
            })()
          }
        />
      )}
    </div>
  );
};

// Mock API functions for features not yet in your Redux slice
const fetchRelatedWork = async (_taskId: string) => {
  // TODO: Implement this API call
  return {
    blocks: [
      { id: '3287', title: 'Health Individual : Unable to create quote in DIC – ...' },
      { id: '3289', title: 'Individuals-Staging-Plans are not fetching for NLG...' }
    ],
    relatesTo: [
      { id: '3291', title: 'Health Individual : Orient Takaful – Unable to log in ...' }
    ]
  };
};



